/**
 * Menus which the iOS app draws natively, as described by the web app (the
 * Web Reader's menu in nativeMenu.ts, the main header in nativeHeader.ts).
 * The app renders whatever these describe, so they change without an app
 * release. Items with `children` are submenus (or sections, if `inline`); the
 * others run their `action` when chosen.
 */
export interface NativeMenuItem {
  title: string;
  subtitle?: string;
  /** An SF Symbol name */
  image?: string;
  checked?: boolean;
  disabled?: boolean;
  /** Deletes or removes something: drawn in red */
  destructive?: boolean;
  /** The action the menu's there for: its image drawn in colour */
  prominent?: boolean;
  /** Identifies the item's action, for the app to report it chosen */
  action?: string;
  children?: NativeMenuItem[];
  inline?: boolean;
}

/** A menu item as the web app builds it, with its action to `perform` */
export interface NativeMenuItemSpec
  extends Omit<NativeMenuItem, "action" | "children"> {
  perform?: () => void;
  children?: NativeMenuItemSpec[];
}

/**
 * The actions of the menus last described, by the ids given to the app. An
 * action's id is its item's path of titles (e.g. "language/I'm learning/
 * Spanish"), not its position: if the menu is rebuilt while the app shows the
 * old one, a choice from it runs the same item's action, or none.
 */
export class NativeActions {
  private actions = new Map<string, () => void>();

  /** Forgets the previous menus' actions, before describing them afresh */
  reset() {
    this.actions.clear();
  }

  add = (id: string, perform: () => void) => {
    this.actions.set(id, perform);
    return id;
  };

  /** The menu as the app draws it, under `path` */
  describe = (items: NativeMenuItemSpec[], path: string): NativeMenuItem[] =>
    items.map(({ perform, children, ...item }) => {
      const id = `${path}/${item.title}`;
      return {
        ...item,
        ...(perform ? { action: this.add(id, perform) } : {}),
        ...(children ? { children: this.describe(children, id) } : {}),
      };
    });

  perform = (id: string) => this.actions.get(id)?.();
}
